import { useEffect, useState } from "react";
import { Trash2, AlertCircle, RefreshCw, Search, X } from "lucide-react";
import { getAllPartnerCoupons, deletePartnerCouponAdmin } from "../service/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const PartnerCoupons = () => {
    const [coupons, setCoupons] = useState<any[]>([]);
    const [filtered, setFiltered] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [deleteModal, setDeleteModal] = useState(false);
    const [deletedId, setDeletedId] = useState<any>(null);

    useEffect(() => {
        fetchAll();
    }, []);

    useEffect(() => {
        const q = search.trim().toLowerCase();
        setFiltered(q ? coupons.filter((c) =>
            c.partner_name?.toLowerCase().includes(q) ||
            c.code?.toLowerCase().includes(q)
        ) : coupons);
    }, [search, coupons]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const res = await getAllPartnerCoupons();
            setCoupons(res.data.data ?? []);
        } catch {
            toast.error("Failed to load partner coupons");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deletePartnerCouponAdmin(deletedId);
            toast.success("Coupon deleted");
            setDeleteModal(false);
            fetchAll();
        } catch {
            toast.error("Failed to delete coupon");
        }
    };

    const isExpired = (dateStr: string) => new Date(dateStr) < new Date();

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Partner Coupons</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Discount codes created by partners for their customers.
                    </p>
                </div>
                <div className="flex gap-2">
                    <div className="relative w-56">
                        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search partner or code…"
                            className="pl-8 text-sm"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                onClick={() => setSearch("")}
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchAll}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10">#</TableHead>
                                    <TableHead>Partner</TableHead>
                                    <TableHead>Code</TableHead>
                                    <TableHead>Discount</TableHead>
                                    <TableHead>Expires</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead className="text-right w-16">Delete</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="py-14 text-center text-muted-foreground">
                                            {search ? "No coupons match your search." : "No partner coupons yet."}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filtered.map((item, index) => {
                                        const expired = isExpired(item.expiry_date);
                                        return (
                                            <TableRow key={item.id}>
                                                <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                                <TableCell>
                                                    <div className="font-medium text-sm">{item.partner_name}</div>
                                                    <div className="text-xs text-muted-foreground">{item.partner_email}</div>
                                                </TableCell>
                                                <TableCell className="font-mono font-semibold">{item.code}</TableCell>
                                                <TableCell>{item.discount}</TableCell>
                                                <TableCell className="tabular-nums text-sm">
                                                    {format(new Date(item.expiry_date), "dd-MM-yyyy")}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={expired ? "destructive" : "default"}>
                                                        {expired ? "Expired" : "Active"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="tabular-nums text-sm text-muted-foreground">
                                                    {format(new Date(item.created_at), "dd-MM-yyyy")}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                                        onClick={() => { setDeletedId(item.id); setDeleteModal(true); }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            {/* Delete confirmation */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this coupon?</DialogTitle>
                        <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PartnerCoupons;
